import React from 'react';
import { Dropdown, Menu, notification } from 'antd';
import {
  isSiemensOpcDcsPlcSubtype,
  openNotificationWithIcon,
  OperationalStatusIcon,
  useAssetConfig,
  useAssetParams,
  useAssetQuery,
} from '@mss/mss-component-library';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { patchAsset } from '../services/update';
import Button from '../../Button';

const capitalize = (word: string) => word[0].toUpperCase() + word.slice(1);

const OperationalStatusMenu = () => {
  const assetParams = useAssetParams();
  const [t] = useTranslation('common');
  const { data: asset, refetch } = useAssetQuery();
  const { matchingConfig } = useAssetConfig(assetParams.assetSubType);
  const changeOperationalStatus = (status: string) => async () => {
    try {
      const response = await patchAsset(assetParams, {
        ...(matchingConfig.form.submitDataParser?.(asset) ?? asset),
        operational_status: status,
      });
      if (!response.ok) {
        throw new Error(response.statusText);
      }

      openNotificationWithIcon(
        notification.success,
        t('Operational status'),
        t('The operational status has been updated successfully')
      );
      await refetch();
    } catch {
      openNotificationWithIcon(
        notification.error,
        t('Operational status'),
        t('The operational status change failed for this asset')
      );
    }
  };

  return (
    <Menu>
      {['operational', 'maintenance', 'disposed'].map(status => {
        return (
          <Menu.Item key={status} onClick={changeOperationalStatus(status)}>
            {t(capitalize(status))}
          </Menu.Item>
        );
      })}
      <></>
    </Menu>
  );
};
type OperationalStatusButtonProps = {
  disabled?: boolean;
};

export default function OperationalStatusButton({
  disabled = false,
}: OperationalStatusButtonProps) {
  const [t] = useTranslation(['common']);
  const { assetSubType } = useAssetParams();
  const { data: asset } = useAssetQuery();
  const operationalStatus = asset?.operational_status ?? '';
  const isPrm = asset?.field_type === 'prm';
  const siemensOpcNoHb = isSiemensOpcDcsPlcSubtype(assetSubType);
  let statusValue: string | number | undefined;
  if (isPrm) {
    statusValue = asset?.asset_data?.status_icon;
  } else if (siemensOpcNoHb) {
    statusValue = undefined;
  } else {
    statusValue = asset?.heartbeat?.state ?? 'turned off';
  }
  return (
    <Dropdown
      overlay={OperationalStatusMenu()}
      trigger={['click']}
      disabled={disabled}
      overlayClassName="ant-dropdown with-arrow with-arrow-right"
      placement="bottomRight"
    >
      <Button className="mr-1">
        <OperationalStatusIcon
          operationalStatus={operationalStatus}
          heartbeatStatus={statusValue}
          heartbeatApplicable={isPrm || !siemensOpcNoHb}
        />
        {t(capitalize(operationalStatus)).toUpperCase()}
        <FontAwesomeIcon icon={faChevronDown} size="xs" className="ml-1" />
      </Button>
    </Dropdown>
  );
}
